import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import {
  Download,
  Calendar,
  Filter,
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIcon,
  BarChart3,
  LineChart as LineChartIcon,
  RefreshCw,
  ChevronDown,
  Printer,
  Share2,
  FileText,
  DollarSign,
  CreditCard,
  Wallet,
  Users,
} from "lucide-react";
import api from "../api/axios";
import CustomSelectDropdown from "../components/CustomSelectDropdown";
import clsx from "clsx";

const Reports = () => {
  const [reportType, setReportType] = useState("monthly");
  const [selectedLedger, setSelectedLedger] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartType, setChartType] = useState("bar"); // bar, line, area
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRangeType, setDateRangeType] = useState("last_6_months");

  // State for data
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [paymentModeData, setPaymentModeData] = useState([]);
  const [ledgerComparison, setLedgerComparison] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [transactionType, setTransactionType] = useState("all");
const [amountRange, setAmountRange] = useState("all");

  // Predefined date ranges
  const dateRanges = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "last_6_months", label: "Last 6 Months" },
    { value: "last_year", label: "Last Year" },
    { value: "custom", label: "Custom Range" },
  ];

  // Chart type options
  const chartTypes = [
    { value: "bar", label: "Bar Chart", icon: BarChart3 },
    { value: "line", label: "Line Chart", icon: LineChartIcon },
    { value: "area", label: "Area Chart", icon: PieChartIcon },
  ];

  // Fetch ledgers for filter dropdown
  useEffect(() => {
    fetchLedgers();
  }, []);

  // Set default dates
  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    setStartDate(sixMonthsAgo.toISOString().split("T")[0]);
    setEndDate(today.toISOString().split("T")[0]);
  }, []);

  // Fetch reports data when filters change
  useEffect(() => {
    if (startDate && endDate) {
      fetchReportsData();
    }
  }, [selectedLedger, startDate, endDate, reportType]);

  const fetchLedgers = async () => {
    try {
      const response = await api.get("/ledgers");
      const ledgersData = response.data.data || response.data;
      setLedgers(Array.isArray(ledgersData) ? ledgersData : []);
    } catch (error) {
      console.error("Error fetching ledgers:", error);
    }
  };

  const fetchReportsData = async () => {
    try {
      setIsLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (selectedLedger && selectedLedger !== "all") {
        params.append("ledgerId", selectedLedger);
      }
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      // Fetch summary data
      const summaryResponse = await api.get(
        `/reports/summary?${params.toString()}`
      );
      setSummary(summaryResponse.data.data || {});

      // Fetch trend data
      const trendsResponse = await api.get(
        `/reports/monthly-trends?${params.toString()}`
      );
      const trends = trendsResponse.data.data || [];

      // Format trend data for charts
      const formattedTrendData = trends.map((item) => ({
        ...item,
        month: item.month || item.date,
        netBalance: (item.income || 0) - (item.expense || 0),
        savingsRate:
          item.expense > 0
            ? (((item.income - item.expense) / item.income) * 100).toFixed(1)
            : 0,
      }));
      setTrendData(formattedTrendData);

      // Fetch category breakdown
      const categoryResponse = await api.get(
        `/reports/category-breakdown?${params.toString()}`
      );
      const categories = categoryResponse.data.data?.categories || [];

      // Colors for pie chart
      const COLORS = [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#8884D8",
        "#82CA9D",
        "#FF6B6B",
        "#4ECDC4",
        "#45B7D1",
        "#96CEB4",
      ];

      const formattedCategories = categories.map((cat, index) => ({
        ...cat,
        name: cat.category,
        value: cat.amount,
        color: COLORS[index % COLORS.length],
      }));
      setCategoryBreakdown(formattedCategories);

      // Fetch payment mode data - FIXED & BEAUTIFUL COLORS (by mode name)
      const paymentResponse = await api.get(
        `/reports/payment-modes?${params.toString()}`
      );
      const payments = paymentResponse.data.data || [];

      const PAYMENT_COLORS = {
        UPI: "#6366F1", // Indigo (matches your screenshot legend)
        "Net Banking": "#8B5CF6", // Purple
        "Bank Transfer": "#10B981", // Emerald Green
        // Add more if needed
        Cash: "#F59E0B",
        Card: "#EF4444",
        Other: "#94A3B8",
      };

      const formattedPayments = payments
        .map((payment) => {
          const totalAmount = (payment.income || 0) + (payment.expense || 0);
          const modeName = payment.mode.trim();

          return {
            name: modeName,
            value: totalAmount,
            income: payment.income || 0,
            expense: payment.expense || 0,
            count: payment.count || 0,
            color: PAYMENT_COLORS[modeName] || PAYMENT_COLORS["Other"],
          };
        })
        .sort((a, b) => b.value - a.value); // Optional: sort by amount (largest first)

      setPaymentModeData(formattedPayments);

      // LEDGER COMPARISON FIXED HERE
      // This route does NOT exist in your backend → 404 error
      // So we safely disable it until backend is ready
      setLedgerComparison([]); // This prevents crash + hides the card
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setIsLoading(false);
    }
  };
  // Handle date range change
  const handleDateRangeChange = (value) => {
    setDateRangeType(value);
    const today = new Date();
    const newStartDate = new Date();

    switch (value) {
      case "today":
        setStartDate(today.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        setStartDate(yesterday.toISOString().split("T")[0]);
        setEndDate(yesterday.toISOString().split("T")[0]);
        break;
      case "last_7_days":
        newStartDate.setDate(today.getDate() - 7);
        setStartDate(newStartDate.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;
      case "last_30_days":
        newStartDate.setDate(today.getDate() - 30);
        setStartDate(newStartDate.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;
      case "last_6_months":
        newStartDate.setMonth(today.getMonth() - 6);
        setStartDate(newStartDate.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;
      case "last_year":
        newStartDate.setFullYear(today.getFullYear() - 1);
        setStartDate(newStartDate.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;
      case "custom":
        // Don't change dates, let user pick
        break;
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (selectedLedger && selectedLedger !== "all") {
        params.append("ledgerId", selectedLedger);
      }
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      params.append("format", format);

      const response = await api.get(`/reports/export?${params.toString()}`);

      if (format === "pdf") {
        // For PDF, we might need a different approach
        alert("PDF export would be implemented here");
        return;
      }

      // Convert to CSV for download
      if (format === "csv") {
        const data = response.data.data;
        const csv = convertToCSV(data);
        downloadFile(
          csv,
          `expense-report-${new Date().toISOString().split("T")[0]}.csv`,
          "text/csv"
        );
      } else if (format === "json") {
        const json = JSON.stringify(response.data.data, null, 2);
        downloadFile(
          json,
          `expense-report-${new Date().toISOString().split("T")[0]}.json`,
          "application/json"
        );
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export data");
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) => Object.values(row).join(",")).join("\n");
    return `${headers}\n${rows}`;
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}:{" "}
              <span className="font-bold">
                ₹{entry.value.toLocaleString("en-IN")}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate statistics for summary cards
  const stats = useMemo(() => {
    const totalIncome = trendData.reduce(
      (sum, item) => sum + (item.income || 0),
      0
    );
    const totalExpense = trendData.reduce(
      (sum, item) => sum + (item.expense || 0),
      0
    );
    const netBalance = totalIncome - totalExpense;
    const savingsRate =
      totalIncome > 0 ? ((netBalance / totalIncome) * 100).toFixed(1) : 0;
    const avgDailyIncome =
      trendData.length > 0 ? totalIncome / trendData.length : 0;
    const avgDailyExpense =
      trendData.length > 0 ? totalExpense / trendData.length : 0;

    return {
      totalIncome,
      totalExpense,
      netBalance,
      savingsRate,
      avgDailyIncome,
      avgDailyExpense,
      transactionCount: summary?.transactionCount || 0,
      categoriesCount: categoryBreakdown.length,
    };
  }, [trendData, summary, categoryBreakdown]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Financial Reports
          </h1>
          <p className="text-gray-500 mt-1">
            Deep insights and analytics for your finances
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={fetchReportsData}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200"
          >
            <RefreshCw size={16} />
            Refresh
          </button> */}
          <div className="flex gap-2">
            <button
              onClick={() => handleExport("json")}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
            >
              <Download size={16} />
              Export
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
            >
              <Printer size={16} />
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all">
              <Share2 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                showFilters
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Filter size={16} />
              Filters
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  showFilters ? "rotate-180" : ""
                }`}
              />
            </button>

            <CustomSelectDropdown
              value={
                selectedLedger === "all"
                  ? "All Books"
                  : ledgers.find((l) => l._id === selectedLedger)?.name ||
                    "All Books"
              }
              options={["All Books", ...ledgers.map((l) => l.name)]}
              onChange={(selectedName) => {
                if (selectedName === "All Books") {
                  setSelectedLedger("all");
                } else {
                  const ledger = ledgers.find((l) => l.name === selectedName);
                  setSelectedLedger(ledger?._id || "all");
                }
              }}
              placeholder="All Books"
            />

          <div className="w-72">
  <CustomSelectDropdown
    label=""
    value={dateRangeType}
    options={dateRanges.map((r) => r.label)}
    onChange={(selectedLabel) => {
      const selected = dateRanges.find((r) => r.label === selectedLabel);
      if (selected) handleDateRangeChange(selected.value);
    }}
    placeholder="Select range"
    leftIcon={Calendar}
    optionIcon={Calendar}
  />
</div>


          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 p-1 rounded-lg">
              {chartTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setChartType(type.value)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      chartType === type.value
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <Icon size={16} />
                  </button>
                );
              })}
            </div>

            <CustomSelectDropdown
              value={
                reportType === "monthly"
                  ? "Monthly View"
                  : reportType === "weekly"
                  ? "Weekly View"
                  : "Daily View"
              }
              options={["Monthly View", "Weekly View", "Daily View"]}
              onChange={(label) => {
                setReportType(
                  label === "Monthly View"
                    ? "monthly"
                    : label === "Weekly View"
                    ? "weekly"
                    : "daily"
                );
              }}
              placeholder="Monthly View"
            />
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type
                </label>
               <CustomSelectDropdown
  value={
    transactionType === "income" ? "Income Only" :
    transactionType === "expense" ? "Expense Only" : "All Types"
  }
  options={["All Types", "Income Only", "Expense Only"]}
  onChange={(label) => {
    const map = { "All Types": "all", "Income Only": "income", "Expense Only": "expense" };
    setTransactionType(map[label]); // You need this state
  }}
  placeholder="All Types"
  className="w-full [&>button]:px-3 [&>button]:py-2 [&>button]:text-sm [&>button]:rounded-lg [&>button]:border [&>button]:border-gray-300 [&>button]:focus:ring-2 [&>button]:focus:ring-indigo-500"
/>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount Range
                </label>
                <CustomSelectDropdown
  value={
    amountRange === "low" ? "Below ₹1,000" :
    amountRange === "medium" ? "₹1,000 - ₹10,000" :
    amountRange === "high" ? "Above ₹10,000" : "All Amounts"
  }
  options={["All Amounts", "Below ₹1,000", "₹1,000 - ₹10,000", "Above ₹10,000"]}
  onChange={(label) => {
    const map = {
      "All Amounts": "all",
      "Below ₹1,000": "low",
      "₹1,000 - ₹10,000": "medium",
      "Above ₹10,000": "high"
    };
    setAmountRange(map[label]); // You need this state
  }}
  placeholder="All Amounts"
  className="w-full [&>button]:px-3 [&>button]:py-2 [&>button]:text-sm [&>button]:rounded-lg [&>button]:border [&>button]:border-gray-300 [&>button]:focus:ring-2 [&>button]:focus:ring-indigo-500"
/>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-5 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <DollarSign size={24} className="opacity-90" />
            <TrendingUp size={20} />
          </div>
          <p className="text-sm font-medium text-emerald-100">Total Income</p>
          <p className="text-3xl font-bold mt-2">
            ₹{stats.totalIncome.toLocaleString("en-IN")}
          </p>
          <div className="h-1 bg-emerald-400/30 rounded-full mt-3">
            <div className="h-1 bg-white rounded-full w-3/4"></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-pink-600 p-5 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <CreditCard size={24} className="opacity-90" />
            <TrendingDown size={20} />
          </div>
          <p className="text-sm font-medium text-rose-100">Total Expense</p>
          <p className="text-3xl font-bold mt-2">
            ₹{stats.totalExpense.toLocaleString("en-IN")}
          </p>
          <div className="h-1 bg-rose-400/30 rounded-full mt-3">
            <div className="h-1 bg-white rounded-full w-2/3"></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-5 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Wallet size={24} className="opacity-90" />
            <TrendingUp size={20} />
          </div>
          <p className="text-sm font-medium text-indigo-100">Net Balance</p>
          <p className="text-3xl font-bold mt-2">
            ₹{Math.abs(stats.netBalance).toLocaleString("en-IN")}
            <span className="text-lg ml-2 opacity-90">
              ({stats.netBalance >= 0 ? "Profit" : "Loss"})
            </span>
          </p>
          <div className="h-1 bg-indigo-400/30 rounded-full mt-3">
            <div className="h-1 bg-white rounded-full w-1/2"></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-5 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Users size={24} className="opacity-90" />
            <FileText size={20} />
          </div>
          <p className="text-sm font-medium text-amber-100">Savings Rate</p>
          <p className="text-3xl font-bold mt-2">{stats.savingsRate}%</p>
          <p className="text-sm opacity-90 mt-2">
            {stats.transactionCount} transactions
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading financial reports...</p>
        </div>
      ) : (
        <>
          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Main Trend Chart */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Financial Trends
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    Income
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                    Expense
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                    Net Balance
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                {chartType === "bar" ? (
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        `₹${(value / 1000).toFixed(0)}K`
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="income"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      name="Income"
                    />
                    <Bar
                      dataKey="expense"
                      fill="#ef4444"
                      radius={[4, 4, 0, 0]}
                      name="Expense"
                    />
                    <Bar
                      dataKey="netBalance"
                      fill="#6366f1"
                      radius={[4, 4, 0, 0]}
                      name="Net Balance"
                    />
                  </BarChart>
                ) : chartType === "line" ? (
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        `₹${(value / 1000).toFixed(0)}K`
                      }
                    />

                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="income"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Income"
                    />
                    <Line
                      type="monotone"
                      dataKey="expense"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Expense"
                    />
                    <Line
                      type="monotone"
                      dataKey="netBalance"
                      stroke="#6366f1"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Net Balance"
                    />
                  </LineChart>
                ) : (
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(value) =>
                        `₹${(value / 1000).toFixed(0)}K`
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="income"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Income"
                    />
                    <Area
                      type="monotone"
                      dataKey="expense"
                      stroke="#ef4444"
                      fill="#ef4444"
                      fillOpacity={0.2}
                      strokeWidth={2}
                      name="Expense"
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Category Breakdown
                </h2>
                <span className="text-sm text-gray-500">
                  {categoryBreakdown.length} categories
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={categoryBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `₹${value.toLocaleString("en-IN")}`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {categoryBreakdown.slice(0, 5).map((item, index) => (
                    <div key={item.name} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {item.name}
                          </span>
                        </div>
                        <span className="text-sm font-bold">
                          ₹{item.value.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-500"
                          style={{
                            width: `${item.percentage}%`,
                            backgroundColor: item.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Mode Distribution */}

            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Payment Methods
              </h2>

              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={paymentModeData.map((d) => ({
                    ...d,
                    value: Number(d.value) || 0, // IMPORTANT FIX
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    domain={[
                      0,
                      Math.max(...paymentModeData.map((d) => Number(d.value))) *
                        1.1,
                    ]}
                    tickFormatter={(v) => {
                      if (v < 1000) return `₹${v}`; // 100 → ₹100
                      return `₹${(v / 1000).toFixed(0)}K`; // 4500 → ₹5K
                    }}
                  />

                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    stroke="#6b7280"
                  />

                  <Tooltip
                    formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                  />

                  <Bar dataKey="value" radius={8} maxBarSize={90}>
                    {paymentModeData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-6 grid grid-cols-3 gap-4">
                {paymentModeData.map((item) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <div>
                      <p className="font-semibold text-sm">{item.name}</p>
                      <p className="text-xs text-gray-600">
                        {item.count} txn • ₹{item.value.toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ledger Comparison (only when all ledgers selected) */}
            {selectedLedger === "all" && ledgerComparison.length > 0 && (
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                  Book Comparison
                </h2>
                <div className="space-y-4">
                  {ledgerComparison.slice(0, 5).map((ledger, index) => (
                    <div key={ledger._id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FileText size={14} className="text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {ledger.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {ledger.transactionCount} transactions
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-bold ${
                              ledger.balance >= 0
                                ? "text-emerald-600"
                                : "text-rose-600"
                            }`}
                          >
                            ₹{Math.abs(ledger.balance).toLocaleString("en-IN")}
                          </p>
                          <p className="text-xs text-gray-500">
                            {ledger.balance >= 0 ? "Profit" : "Loss"}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                          Income: ₹{ledger.totalIncome?.toLocaleString("en-IN")}
                        </span>
                        <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded">
                          Expense: ₹
                          {ledger.totalExpense?.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Data Table View */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Detailed Transaction Data
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {trendData.length} periods analyzed
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Income
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expense
                    </th>
                    <th className="px6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Savings Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {trendData.slice(0, 10).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {row.month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-emerald-600 font-medium">
                        ₹{row.income.toLocaleString("en-IN")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-rose-600 font-medium">
                        ₹{row.expense.toLocaleString("en-IN")}
                      </td>
                      <td
                        className={clsx(
                          "px-6 py-4 whitespace-nowrap text-sm font-medium",
                          row.netBalance >= 0
                            ? "text-emerald-600"
                            : "text-rose-600"
                        )}
                      >
                        ₹{Math.abs(row.netBalance).toLocaleString("en-IN")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {row.savingsRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
